<template>
  <section id="about-me" class="about-section">
    <div class="container py-5">
      <div class="about-intro" data-aos="fade-up" data-aos-duration="900">
        <div class="about-intro__copy">
          <span class="about-kicker"><span aria-hidden="true"></span>{{ data.label }}</span>
          <h2>{{ data.title }}</h2>
          <p v-for="paragraph in data.intro" :key="paragraph">{{ paragraph }}</p>
        </div>
      </div>

      <div ref="aboutStats" class="about-stats" data-aos="fade-up" data-aos-duration="950">
        <div v-for="(stat, index) in data.stats" :key="stat.label" class="about-stat">
          <strong>{{ displayedStats[index] }}{{ stat.suffix }}</strong>
          <span>{{ stat.label }}</span>
        </div>
      </div>

      <div
        ref="aboutGrid"
        class="about-grid"
        @scroll.passive="updateSlide"
        @pointerdown="startDrag"
        @pointermove="drag"
        @pointerup="endDrag"
        @pointercancel="endDrag"
        @pointerleave="endDrag"
      >
        <article
          v-for="(card, index) in data.cards"
          :key="card.title"
          :class="['about-card', { 'about-card--focus': index === 0 }]"
          data-aos="fade-up"
          :data-aos-duration="1000 + index * 100"
        >
          <span class="about-card__icon" :class="card.icon" aria-hidden="true"></span>
          <h3>{{ card.title }}</h3>
          <p v-for="paragraph in card.paragraphs" :key="paragraph">{{ paragraph }}</p>
        </article>
      </div>

      <div class="slider-pagination" :aria-label="data.paginationLabel">
        <button
          v-for="(_, index) in data.cards"
          :key="index"
          type="button"
          :class="{ active: currentSlide === index }"
          :aria-label="`Show about slide ${index + 1}`"
          @click="goToSlide(index)"
        ></button>
      </div>
    </div>
  </section>
</template>

<script>
export default {
  name: "portabout",
  props: {
    data: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      currentSlide: 0,
      rotationTimer: null,
      isDragging: false,
      dragStartX: 0,
      dragStartScrollLeft: 0,
      displayedStats: [],
      counterFrame: null,
      counterObserver: null,
    };
  },
  mounted() {
    this.rotationTimer = window.setInterval(this.rotateSlide, 4500);
    this.displayedStats = this.data.stats.map(() => 1);

    if (window.IntersectionObserver) {
      this.counterObserver = new IntersectionObserver((entries) => {
        if (!entries[0].isIntersecting) return;
        this.startCounters();
        this.counterObserver.disconnect();
      }, { threshold: 0.25 });
      this.counterObserver.observe(this.$refs.aboutStats);
    } else {
      this.startCounters();
    }
  },
  beforeUnmount() {
    window.clearInterval(this.rotationTimer);
    window.cancelAnimationFrame(this.counterFrame);
    if (this.counterObserver) this.counterObserver.disconnect();
  },
  methods: {
    startCounters() {
      const startTime = performance.now();

      const animate = (currentTime) => {
        const progress = Math.min((currentTime - startTime) / 3000, 1);
        const easedProgress = 1 - Math.pow(1 - progress, 3);
        this.displayedStats = this.data.stats.map((stat) => (
          Math.max(1, Math.floor(1 + (stat.target - 1) * easedProgress))
        ));

        if (progress < 1) {
          this.counterFrame = window.requestAnimationFrame(animate);
        }
      };

      this.counterFrame = window.requestAnimationFrame(animate);
    },
    updateSlide(event) {
      const slides = Array.from(event.target.children);
      const closestSlide = slides.reduce((closest, slide, index) => {
        const distance = Math.abs(slide.offsetLeft - event.target.scrollLeft);
        return distance < closest.distance ? { distance, index } : closest;
      }, { distance: Infinity, index: 0 });
      this.currentSlide = closestSlide.index;
    },
    goToSlide(index) {
      const grid = this.$refs.aboutGrid;
      const slide = grid && grid.children[index];
      if (!slide) return;
      grid.scrollTo({ left: slide.offsetLeft, behavior: "smooth" });
      this.currentSlide = index;
    },
    startDrag(event) {
      const grid = this.$refs.aboutGrid;
      if (!grid || grid.scrollWidth <= grid.clientWidth) return;
      this.isDragging = true;
      this.dragStartX = event.clientX;
      this.dragStartScrollLeft = grid.scrollLeft;
      grid.setPointerCapture(event.pointerId);
    },
    drag(event) {
      if (!this.isDragging) return;
      event.preventDefault();
      this.$refs.aboutGrid.scrollLeft = this.dragStartScrollLeft - (event.clientX - this.dragStartX);
    },
    endDrag(event) {
      if (!this.isDragging) return;
      this.isDragging = false;
      const grid = this.$refs.aboutGrid;
      if (grid && event.pointerId !== undefined && grid.hasPointerCapture(event.pointerId)) {
        grid.releasePointerCapture(event.pointerId);
      }
    },
    rotateSlide() {
      if (window.innerWidth > 700 || this.currentSlide >= this.data.cards.length - 1) {
        if (this.currentSlide >= this.data.cards.length - 1) {
          window.clearInterval(this.rotationTimer);
        }
        return;
      }
      this.goToSlide(this.currentSlide + 1);
    },
  },
};
</script>

<style lang="scss">
@import "../assets/styles/_website.scss";

.about-section {
  background:
    radial-gradient(circle at 92% 12%, rgba(110, 195, 197, 0.18), transparent 28%),
    linear-gradient(180deg, #f7f2eb 0%, #f4efe9 100%);
  color: $dark-blue;
  padding: 1.5rem 0;

  .about-intro {
    max-width: 68rem;
    display: flex;
    align-items: flex-start;
    margin-bottom: 1.75rem;

    &__copy {
      max-width: 52rem;
    }

    &__signal {
      display: flex;
      align-items: baseline;
      gap: 0.7rem;
      padding: 0 0 0.35rem 1.5rem;
      border-left: 2px solid $blue-green;

      @media (max-width: 700px) {
        padding: 0.75rem 0 0.75rem 1rem;
      }
    }

    &__number {
      color: $blue-green;
      font-family: $primary_font;
      font-size: 3.8rem;
      font-weight: 800;
      line-height: 0.9;
    }

    &__label {
      color: $muted;
      font-size: 0.82rem;
      font-weight: 700;
      letter-spacing: 0.08em;
      line-height: 1.35;
      text-transform: uppercase;
    }
  }

  .about-kicker {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    margin-bottom: 1.5rem;
    color: $blue-green;
    font-size: 0.74rem;
    font-weight: 800;
    letter-spacing: 0.16em;
    text-transform: uppercase;

    span {
      display: inline-block;
      width: 2.5rem;
      height: 1px;
      background: $blue-green;
    }
  }

  h2 {
    max-width: none;
    margin: 0.7rem 0 1rem;
    color: $dark-blue;
    font-size: clamp(2.2rem, 4vw, 3.5rem);
    font-weight: 800;
    letter-spacing: -0.06em;
    line-height: 0.98;
  }

  .about-intro p {
    max-width: none;
    color: $muted;
    line-height: 1.75;
    margin: 0 0 0.8rem;
  }

  .about-stats {
    display: flex;
    align-items: center;
    gap: 4.5rem;
    margin: 0 0 2.75rem;
    padding: 1.35rem 0 1.25rem;
    border-top: 1px solid rgba(10, 37, 65, 0.14);

    @media (max-width: 520px) {
      align-items: flex-start;
      flex-direction: column;
      gap: 1.25rem;
    }
  }

  .about-stat {
    display: flex;
    align-items: baseline;
    gap: 0.8rem;

    strong {
      color: $blue-green;
      font-family: $primary_font;
      font-size: clamp(2rem, 4vw, 3rem);
      font-weight: 800;
      line-height: 1;
    }

    span {
      color: $dark-blue;
      font-size: 0.9rem;
      font-weight: 700;
      letter-spacing: 0.04em;
      text-transform: uppercase;
    }
  }

  .about-grid {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 1rem;

    @media (max-width: 950px) {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    @media (max-width: 700px) {
      display: flex;
      gap: 0.9rem;
      width: calc(100% + 1rem);
      margin-right: -1rem;
      overflow-x: scroll;
      padding-right: 1rem;
      scroll-snap-type: x mandatory;
      scrollbar-width: none;
      -webkit-overflow-scrolling: touch;
      touch-action: pan-x;
      cursor: grab;

      &:active {
        cursor: grabbing;
      }

      &::-webkit-scrollbar {
        display: none;
      }
    }
  }

  .about-card {
    min-height: 265px;
    padding: 1.75rem;
    background: rgba(255, 255, 255, 0.72);
    border: 1px solid rgba(10, 37, 65, 0.1);
    border-radius: 1rem;
    box-shadow: 0 14px 30px rgba(10, 37, 65, 0.06);

    @media (max-width: 700px) {
      flex: 0 0 86%;
      scroll-snap-align: start;
    }

    &--focus {
      background: $dark-blue;
    }

    &__icon {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 2.6rem;
      height: 2.6rem;
      margin-bottom: 1.4rem;
      border-radius: 0.75rem;
      background: rgba(110, 195, 197, 0.18);
      color: $blue-green;
    }

    &__eyebrow {
      display: block;
      margin-bottom: 0.65rem;
    }

    h3 {
      margin: 0 0 0.8rem;
      color: $dark-blue;
      font-size: 1.55rem;
      font-weight: 800;
      letter-spacing: -0.04em;
      line-height: 1.05;
    }

    p {
      color: $muted;
      line-height: 1.65;
      margin: 0 0 0.85rem;

      &:last-child {
        margin-bottom: 0;
      }
    }

    .about-capabilities {
      display: grid;
      gap: 0.7rem;
      margin: 0 0 1rem;
      padding: 0;
      list-style: none;

      li {
        display: grid;
        gap: 0.15rem;
        padding-left: 0.8rem;
        border-left: 2px solid rgba(17, 97, 126, 0.28);

        strong {
          color: $dark-blue;
          font-size: 0.92rem;
          line-height: 1.25;
        }

        span {
          color: $muted;
          font-size: 0.88rem;
          line-height: 1.45;
        }
      }
    }

    &--focus {
      h3 {
        color: white;
      }

      p {
        color: rgba(255, 255, 255, 0.88);
      }
    }
  }

  .slider-pagination {
    display: none;
    width: 100%;
    gap: 0.35rem;
    margin-top: 1rem;

    button {
      flex: 1 1 0;
      width: auto;
      height: 3px;
      padding: 0;
      border: 0;
      background: rgba(10, 37, 65, 0.2);
      cursor: pointer;
      transition: width 0.2s ease, background 0.2s ease;

      &.active {
        background: $blue-green;
      }
    }

    @media (max-width: 700px) {
      display: flex;
    }
  }

  .about-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 0.45rem;
    margin-top: 1rem;

    span {
      padding: 0.4rem 0.6rem;
      border: 1px solid rgba(17, 97, 126, 0.16);
      border-radius: 999px;
      color: $blue-green;
      font-size: 0.74rem;
      font-weight: 700;
    }
  }
}
</style>